import React, { ReactElement } from 'react'
import styles from '@/components/ExamChangeGuidePage/ExamChangeInfo/ExamChangeInfoSharedStyling.module.scss'
import { reusableCssClass } from '@/utils/reusableCssClasses'

export const Fall2026: React.FC = (): ReactElement => {
    return (
        <div className={styles.examChangeGuideSharedComponent} id={'fall2026'}>
            <div className={styles.examChangeGuidePageTop}>
                <h2>Fall 2026</h2>
                <h3>Summary</h3>
                <div>
                    The SOA has released updated syllabi effective for the Nov
                    2026 / Mar 2027 / Jul 2027 sittings of the FSA Group and
                    Health exams (CP321 is only offered in the Nov 2026 and Jul
                    2027 sittings). The updated syllabi can be found here:
                    <ul>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2026/gh-101/2026-2027-gh-101-syllabus.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                GH101: Benefits and Pricing
                            </a>
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2026/gh-201u/2026-2027-gh-201u-syllabus.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                GH201: Valuation and Regulation, US
                            </a>
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2026/gh-301/2026-2027-gh-301-syllabus.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                GH301: Health Analytics and Management
                            </a>
                        </li>
                        <li>
                            <a
                                href={
                                    'https://www.soa.org/globalassets/assets/files/edu/2026/cp-321/2026-2027-cp-321-syllabus.pdf'
                                }
                                target={'_blank'}
                                rel={'noreferrer'}
                                className={reusableCssClass.clickableText}
                            >
                                CP321: Disability, Long-Term Care, and
                                Long-Duration Health Contracts
                            </a>
                        </li>
                    </ul>
                    This is the first syllabus refresh since the Fall 2025
                    restructuring, and the changes are modest: a handful of
                    readings were added, removed, or replaced, with no major
                    shift in the total amount of content. Two changes are worth
                    calling out up front:
                    <ul>
                        <li>
                            <b>Case studies</b> - <b>Update (Aug 2026):</b> the
                            syllabi as originally released stated that GH101 and
                            GH301 would include a case study provided with the
                            examination. The SOA has since revised both syllabi,
                            which now state that a case study will not be
                            provided. As it stands, none of the four exams has a
                            case study.
                        </li>
                        <li>
                            <b>
                                Individual Health Insurance, 3rd Edition (Leida,
                                2026)
                            </b>{' '}
                            - the textbook was updated from the 2nd Edition
                            (Bluhm & Leida, 2015), affecting both GH101 and
                            CP321. The chapters covered are largely the same,
                            but readings now reference the new edition.
                        </li>
                    </ul>
                    Find the details on the changes for each exam below.
                    <br />
                    <br />
                    GH101 has the following changes:
                    <ul>
                        <li>
                            <b>Added</b> - Closing the Analytical Loop:
                            Technology Assessments and Actuarial Methods (SOA
                            Research Institute, Jan 2025), under the Manual
                            Rates topic.
                        </li>
                        <li>
                            <b>Added</b> - Modeling Effects of Enrollee Choice
                            (SOA, Jan 2021), under the Underwriting and Funding
                            topic.
                        </li>
                        <li>
                            <b>Replaced</b> - GH101-100-25 (Ch. 5 of Individual
                            Health Insurance, 2nd Edition) was replaced by
                            GH101-100-26, which covers the same Chapter 5 in the
                            new 3rd Edition.
                        </li>
                        <li>
                            <b>Format Change (Reversed)</b> - The originally
                            released syllabus stated that a case study would be
                            provided with the examination, but the SOA has since
                            revised the syllabus and removed it. No case study
                            will be provided.
                        </li>
                    </ul>
                    GH201 has the following changes:
                    <ul>
                        <li>
                            <b>Added</b> - GH201-407-26: Supplement to the
                            Premium Deficiency Reserves Discussion Paper, under
                            the Reserving topic.
                        </li>
                        <li>
                            <b>Added</b> - GH201-408-26: A Tale of Two Systems,
                            a reading on the regulation of gene therapies, under
                            the Regulation topic.
                        </li>
                        <li>
                            <b>Moved</b> - The Risk-Based Capital readings
                            (Group Insurance Ch. 41 and GH201-402-25: RBC
                            Calculation Examples) moved from the Regulation
                            topic to the Financial Statements topic, which
                            gained a new learning outcome and saw its weight
                            increase from 15%-30% to 25%-35%.
                        </li>
                        <li>
                            <b>Trivial Renaming</b> - ASOP 7 is now titled
                            &quot;Life or Health Cash Flow Analysis&quot;.
                        </li>
                    </ul>
                    GH301 has the following changes:
                    <ul>
                        <li>
                            <b>Added</b> - GH301-112-26: PY2025 ACO REACH RTA
                            Tailwind.
                        </li>
                        <li>
                            <b>Added</b> - GH301-113-26: ACO REACH PY2026
                            Explained.
                        </li>
                        <li>
                            <b>Added</b> - Reimagining Pharmacy Financing (SOA
                            Research Institute, Mar 2024, Section 3, with
                            Section 2 as background).
                        </li>
                        <li>
                            <b>Removed</b> - Healthcare Risk Adjustment and
                            Predictive Modeling, Ch. 22: Accountable Care
                            Organizations.
                        </li>
                        <li>
                            <b>Removed</b> - GH301-101-25: Avoiding Unintended
                            Consequences in ACO Payment Model.
                        </li>
                        <li>
                            <b>Removed</b> - Valuation of Care Management
                            Vendors (Health Watch, May 2020).
                        </li>
                        <li>
                            <b>Format Change (Reversed)</b> - The originally
                            released syllabus stated that a case study would be
                            provided with the examination, but the SOA has since
                            revised the syllabus and removed it. No case study
                            will be provided.
                        </li>
                    </ul>
                    CP321 has the following changes:
                    <ul>
                        <li>
                            <b>Replaced</b> - The Individual Health Insurance
                            readings (Ch. 2, 4, and 6) now reference the 3rd
                            Edition, with the covered sections narrowed to
                            Sections 2.7-2.9 of Ch. 2 and Sections 4.1-4.5 of
                            Ch. 4.
                        </li>
                        <li>
                            <b>Replaced</b> - CP321-103-25 (LTCI MSA Framework
                            working draft) was replaced by CP321-103-26, the
                            finalized NAIC Long-Term Care Insurance Multistate
                            Rate Review Framework (Dec 2025, Sections V-VI).
                        </li>
                        <li>
                            <b>Added</b> - IFRS 17 Insurance Contract Examples,
                            under the Financial Statements topic (now renamed
                            &quot;Insurer Financial Statements&quot;).
                        </li>
                        <li>
                            <b>Removed</b> - CP321-112-25: Comparison of IAS 19,
                            Rev. 2011 with FASB ASC 715.
                        </li>
                    </ul>
                    <div>
                        We are in the process of updating our study materials to
                        reflect the new syllabi. If you have any questions about
                        the changes, do not hesitate to reach out to us at
                        support@justenoughprep.com, and we will be happy to
                        help.
                    </div>
                </div>
            </div>
        </div>
    )
}
